/**
 * Descriptor-driven Racks Summary source (#997 — Story 3.3).
 *
 * Reproduces the bespoke `getRackFormSummaryDefinitions()` row set and per-unit
 * value/title projection from the Racks descriptor + the shared Summary factory,
 * byte-identically against the Story 3.2 golden masters
 * (`racks-summary-rows.json`, `racks-summary-filled.json`). This is the
 * engine-driven replacement Story 3.3 re-points the Summary and the golden tests
 * at — same oracle, new implementation.
 *
 * The three subsection markers (`racks`, `rackPowerInformation`,
 * `additionalInformation`) are produced by THREE scoped
 * `buildEditorSubsectionSummaryRows` calls, one per group, each stamping its
 * `subsectionId` on its first row (the generic factory marks the first row only).
 * `labelOverrides` cover the two fields whose bespoke summary label diverged from
 * the registry (`rackName` → "Rack Name", `rackStatus` → the Rack-type label);
 * the identity fields `rackName`/`rackStatus` are not registry fields, so their
 * labels come from the override map.
 */

import { RACKS_DESCRIPTOR } from './racks.descriptor';
import { RACK_EDIT_RACK_TYPE_LABEL } from '../../constants/rackConfigurationsSubsectionCopy';
import { buildEditorSubsectionSummaryRows } from '../fields/projections/editorSubsectionSummaryRows';
import { racksSummaryRowValue } from '../fields/projections/summaryRowValues';
import {
  RACK_MODAL_ADDITIONAL_FIELDS,
  RACK_MODAL_POWER_FIELDS,
  RACK_MODAL_RACKS_FIELDS,
} from '../fields/requestFieldRegistry';

import type { SummaryCardRow } from '../../summary/summaryCardSourceTypes';
import type { RackConfiguration } from '../../types/rack';
import type { SubsectionFieldDescriptor } from '../../utils/subsectionHeaderMetrics';

const RBAC_ANCHOR = RACKS_DESCRIPTOR.permissionRef.rbacAnchorDataField;

/** Registry field metadata (labels) the factory resolves per field. */
const REGISTRY_FIELDS = RACKS_DESCRIPTOR.fields;

/** Labels the bespoke `rackFormSummaryLabel` overrode vs the plain registry label. */
const RACKS_GROUP_LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  rackName: 'Rack Name',
  rackStatus: RACK_EDIT_RACK_TYPE_LABEL,
};

function buildGroup(
  editorFields: readonly SubsectionFieldDescriptor[],
  subsectionId: string,
  labelOverrides?: Readonly<Record<string, string>>,
): SummaryCardRow[] {
  return buildEditorSubsectionSummaryRows({
    editorFields,
    registryFields: REGISTRY_FIELDS,
    rbacAnchorDataField: RBAC_ANCHOR,
    subsectionId,
    labelOverrides,
  });
}

/**
 * Full ordered Racks Summary row set built from the descriptor via three scoped
 * factory calls — byte-identical to the bespoke `getRackFormSummaryDefinitions()`.
 */
export function getRacksDescriptorSummaryDefinitions(): SummaryCardRow[] {
  return [
    ...buildGroup(
      RACK_MODAL_RACKS_FIELDS,
      'racks',
      RACKS_GROUP_LABEL_OVERRIDES,
    ),
    ...buildGroup(RACK_MODAL_POWER_FIELDS, 'rackPowerInformation'),
    ...buildGroup(RACK_MODAL_ADDITIONAL_FIELDS, 'additionalInformation'),
  ];
}

/**
 * Per-unit rack summary value — delegates to the shared `racksSummaryRowValue`
 * (Existing/New, Yes/No, merged additional-info). Re-homed here so the Summary
 * value path no longer routes through the bespoke `racksSummaryDisplay` module.
 */
export function racksDescriptorUnitRowValue(
  rack: RackConfiguration,
  dataField: string,
): string | number | boolean | null | undefined {
  return racksSummaryRowValue(
    rack as unknown as Record<string, unknown>,
    dataField,
  );
}

/**
 * Resolve a rack's display name (user name → manufacturer + model → platform →
 * "Rack N"). Re-homed from `rackListUtils.getRackDisplayName` so the Summary
 * title no longer depends on the bespoke `components/racks` module.
 */
export function resolveRackSummaryDisplayName(
  rack: RackConfiguration,
): string | null {
  const rackName = rack.rackName?.trim();
  if (rackName) {
    return rackName;
  }
  const titled = [rack.rackPduManufacturer, rack.rackPduModel]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
  if (titled) {
    return titled;
  }
  const platform = rack.rackPlatform?.trim();
  if (platform) {
    return platform;
  }
  if (rack.displayOrder != null && rack.displayOrder > 0) {
    return `Rack ${rack.displayOrder}`;
  }
  return null;
}

/** Per-unit Summary card title — `Existing/New — <name>` (fallback `… rack N`). */
export function getRacksDescriptorUnitTitle(
  rack: RackConfiguration,
  index: number,
): string {
  const status = rack.rackStatus === 'existing' ? 'Existing' : 'New';
  const name = resolveRackSummaryDisplayName(rack);
  return name ? `${status} — ${name}` : `${status} rack ${index + 1}`;
}
